import { useState, useEffect } from "react";
import { safeHref } from "../lib/safeUrl";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { API, useAuth } from "../App";

/**
 * WhatsNewBanner — small dismissible banner shown to logged-in users on
 * their next visit after a new release-notes entry is published.
 *
 * - Calls GET /api/whats-new/active on mount; renders only if the backend
 *   returns an entry the current user hasn't dismissed yet.
 * - Dismissal is per-user persisted (POST /api/whats-new/dismiss/{id}).
 * - Items in `entry.items` support bold markdown via **double-asterisks**.
 */

const renderItem = (text) => {
  // Convert **bold** segments to <strong>; everything else plaintext.
  // Index keys are safe here: parts come from a deterministic split of a
  // static string and never reorder.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      // eslint-disable-next-line react/no-array-index-key
      ? <strong key={`b-${i}`} className="font-semibold text-white">{p.slice(2, -2)}</strong>
      // eslint-disable-next-line react/no-array-index-key
      : <span key={`t-${i}`}>{p}</span>
  );
};

const WhatsNewBanner = () => {
  const { user } = useAuth();
  const [entry, setEntry] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    axios.get(`${API}/whats-new/active`)
      .then(r => {
        if (cancelled) return;
        if (r.data?.entry) {
          setEntry(r.data.entry);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = async () => {
    if (!entry || dismissing) return;
    setDismissing(true);
    setOpen(false);
    try {
      await axios.post(`${API}/whats-new/dismiss/${entry.entry_id}`);
    } catch {
      // soft-fail: banner is already hidden client-side
    }
  };

  if (!entry) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
          className="relative bg-gradient-to-r from-[#6B4E8A] to-[#553D6F] text-white border-b border-[#553D6F]"
          data-testid="whats-new-banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-start sm:items-center gap-3 sm:gap-4">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-[#8DB5A0]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-[11px] uppercase tracking-widest text-white/60">{entry.version}</p>
                <p className="text-sm font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{entry.title}</p>
              </div>
              {expanded && (
                <ul className="mt-2 space-y-1 text-xs text-white/90 leading-relaxed">
                  {entry.items.map((it, i) => (
                    <li key={`${entry.entry_id || "wn"}-${i}`} className="flex items-start gap-2">
                      <span className="text-[#8DB5A0] mt-0.5">·</span>
                      <span>{renderItem(it)}</span>
                    </li>
                  ))}
                  {entry.cta_url && entry.cta_label && (
                    <li className="pt-1">
                      <a
                        href={safeHref(entry.cta_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-3 py-1 rounded-full bg-white text-[#6B4E8A] text-xs font-medium hover:bg-[#FAF7F2]"
                      >
                        {entry.cta_label} →
                      </a>
                    </li>
                  )}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="text-xs text-white/80 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors shrink-0"
              data-testid="whats-new-toggle"
            >
              {expanded ? "Hide" : "What's new"}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="p-1.5 rounded-full hover:bg-white/15 transition-colors shrink-0"
              aria-label="Dismiss"
              data-testid="whats-new-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsNewBanner;
